// /admin/configuracion-bancaria — edita las 9 keys BANK_* en app_config
// sin tocar código. Solo superadmin.
//
// El cliente con transferencia habilitada ve estos datos en /cliente/pagar-pedido/{id}

import { useEffect, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/AuthContext'

const CAMPOS = [
  { key: 'BANK_BANCO', label: 'Banco', placeholder: 'Itaú · BROU · Santander · BBVA', tipo: 'text' },
  { key: 'BANK_TITULAR', label: 'Titular', placeholder: 'LIMPACK SAS', tipo: 'text' },
  { key: 'BANK_RUT', label: 'RUT', placeholder: '220903660013', tipo: 'text' },
  { key: 'BANK_TIPO_CUENTA', label: 'Tipo de cuenta', placeholder: 'Caja de ahorro · Cuenta corriente', tipo: 'text' },
  { key: 'BANK_MONEDA', label: 'Moneda', placeholder: 'UYU · USD', tipo: 'text' },
  { key: 'BANK_NUMERO', label: 'Número de cuenta', placeholder: '1234567890', tipo: 'text', destacado: true },
  { key: 'BANK_SUCURSAL', label: 'Sucursal (opcional)', placeholder: 'Sucursal Centro', tipo: 'text' },
  { key: 'BANK_ALIAS', label: 'Alias bancario (opcional)', placeholder: 'Mi BROU o alias custom', tipo: 'text' },
  { key: 'BANK_INSTRUCCIONES', label: 'Instrucciones para el cliente', placeholder: 'Texto que ve el cliente cuando va a transferir', tipo: 'textarea' },
]

export default function ConfiguracionBancaria() {
  const { profile } = useAuth()
  const [valores, setValores] = useState({})
  const [originales, setOriginales] = useState({})
  const [loading, setLoading] = useState(true)
  const [guardando, setGuardando] = useState(false)
  const [mensaje, setMensaje] = useState(null)

  const esSuperadmin = profile?.role === 'superadmin'

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    const { data } = await supabase
      .from('app_config')
      .select('key, value')
      .like('key', 'BANK_%')
    const map = {}
    ;(data || []).forEach(c => {
      const v = typeof c.value === 'string' ? c.value : c.value
      map[c.key] = v
    })
    setValores(map)
    setOriginales(map)
    setLoading(false)
  }

  function cambio(key, val) {
    setValores(prev => ({ ...prev, [key]: val }))
    setMensaje(null)
  }

  async function guardar() {
    setGuardando(true)
    setMensaje(null)
    try {
      const cambios = []
      for (const [key, val] of Object.entries(valores)) {
        if (originales[key] !== val) {
          cambios.push({ key, value: JSON.stringify(val) })
        }
      }
      if (cambios.length === 0) {
        setMensaje({ tipo: 'info', texto: 'No hay cambios que guardar.' })
        setGuardando(false)
        return
      }

      // UPSERT cada clave
      for (const c of cambios) {
        const { error } = await supabase
          .from('app_config')
          .update({ value: JSON.parse(c.value), updated_at: new Date().toISOString() })
          .eq('key', c.key)
        if (error) throw error
      }

      setOriginales({ ...valores })
      setMensaje({ tipo: 'ok', texto: `✓ Guardados ${cambios.length} cambio${cambios.length === 1 ? '' : 's'}.` })
    } catch (e) {
      setMensaje({ tipo: 'error', texto: 'Error: ' + e.message })
    }
    setGuardando(false)
  }

  const haCambios = JSON.stringify(valores) !== JSON.stringify(originales)

  return (
    <AdminLayout>
      <div className="cb-wrap">
        <div className="cb-headline">
          <h1>🏦 Configuración bancaria</h1>
          <p>Datos de la cuenta donde reciben las transferencias bancarias de clientes habilitados. Lo que cargues acá lo ven los clientes en su pantalla de pago. Editable solo por superadmin.</p>
        </div>

        {!esSuperadmin && (
          <div className="cb-warn">
            ⚠ Solo un superadmin puede editar estos datos. Estás en modo lectura.
          </div>
        )}

        {loading ? (
          <div className="cb-loading">Cargando…</div>
        ) : (
          <div className="cb-form">
            {CAMPOS.map(c => (
              <div key={c.key} className={`cb-field ${c.destacado ? 'cb-field-destacado' : ''}`}>
                <label>{c.label}</label>
                {c.tipo === 'textarea' ? (
                  <textarea
                    value={valores[c.key] ?? ''}
                    onChange={e => cambio(c.key, e.target.value)}
                    placeholder={c.placeholder}
                    rows={3}
                    disabled={!esSuperadmin}
                  />
                ) : (
                  <input
                    type="text"
                    value={valores[c.key] ?? ''}
                    onChange={e => cambio(c.key, e.target.value)}
                    placeholder={c.placeholder}
                    disabled={!esSuperadmin}
                  />
                )}
                <div className="cb-field-key">app_config.{c.key}</div>
              </div>
            ))}

            {mensaje && (
              <div className={`cb-msg cb-msg-${mensaje.tipo}`}>{mensaje.texto}</div>
            )}

            {esSuperadmin && (
              <div className="cb-actions">
                <button
                  className="cb-btn-primary"
                  onClick={guardar}
                  disabled={guardando || !haCambios}
                >
                  {guardando ? 'Guardando…' : haCambios ? '✓ Guardar cambios' : 'Sin cambios'}
                </button>
              </div>
            )}
          </div>
        )}

        <div className="cb-preview">
          <h3>👁 Vista previa para el cliente</h3>
          <div className="cb-preview-card">
            <div className="cb-row"><strong>Banco</strong><span>{valores.BANK_BANCO || '—'}</span></div>
            <div className="cb-row"><strong>Titular</strong><span>{valores.BANK_TITULAR || '—'}</span></div>
            <div className="cb-row"><strong>RUT</strong><span>{valores.BANK_RUT || '—'}</span></div>
            <div className="cb-row"><strong>Tipo</strong><span>{valores.BANK_TIPO_CUENTA || '—'} · {valores.BANK_MONEDA || 'UYU'}</span></div>
            <div className="cb-row cb-row-highlight">
              <strong>N° cuenta</strong>
              <span style={{ fontFamily: 'monospace', fontSize: 15 }}>{valores.BANK_NUMERO || '—'}</span>
            </div>
            {valores.BANK_SUCURSAL && <div className="cb-row"><strong>Sucursal</strong><span>{valores.BANK_SUCURSAL}</span></div>}
            {valores.BANK_ALIAS && <div className="cb-row"><strong>Alias</strong><span>{valores.BANK_ALIAS}</span></div>}
            {valores.BANK_INSTRUCCIONES && (
              <p className="cb-preview-instr">{valores.BANK_INSTRUCCIONES}</p>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .cb-wrap { max-width: 800px; margin: 0 auto; padding: 16px }
        .cb-headline h1 { font-size: 22px; font-weight: 900; color: #1A3461; letter-spacing: -.01em }
        .cb-headline p { font-size: 13px; color: #64748b; margin-top: 4px; line-height: 1.5 }

        .cb-loading { padding: 40px; text-align: center; color: #94a3b8 }
        .cb-warn { background: #fef3c7; border: 1px solid #fcd34d; color: #78350f; padding: 12px 14px; border-radius: 10px; margin: 18px 0; font-size: 13px; font-weight: 600 }

        .cb-form { background: #fff; border: 1px solid #e2e8f0; border-radius: 14px; padding: 22px; margin-top: 18px }
        .cb-field { margin-bottom: 16px }
        .cb-field-destacado { background: #f0f9ff; border: 1px solid #bae6fd; border-radius: 10px; padding: 12px; margin-left: -12px; margin-right: -12px }
        .cb-field label { display: block; font-size: 12px; font-weight: 800; color: #475569; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 6px }
        .cb-field input, .cb-field textarea { width: 100%; padding: 10px 12px; border: 1px solid #cbd5e1; border-radius: 8px; font-size: 14px; font-family: inherit; color: #1a3461 }
        .cb-field input:focus, .cb-field textarea:focus { outline: 0; border-color: #00A8B5; box-shadow: 0 0 0 3px rgba(0,168,181,0.15) }
        .cb-field input:disabled, .cb-field textarea:disabled { background: #f1f5f9; color: #94a3b8 }
        .cb-field-key { font-size: 10px; color: #94a3b8; font-family: monospace; margin-top: 4px }

        .cb-msg { padding: 10px 14px; border-radius: 8px; font-size: 13px; font-weight: 600; margin-top: 10px }
        .cb-msg-ok { background: #d1fae5; border: 1px solid #6ee7b7; color: #065f46 }
        .cb-msg-error { background: #fee2e2; border: 1px solid #fca5a5; color: #991b1b }
        .cb-msg-info { background: #dbeafe; border: 1px solid #93c5fd; color: #1e40af }

        .cb-actions { display: flex; justify-content: flex-end; margin-top: 18px }
        .cb-btn-primary { background: #1A3461; color: #fff; border: 0; padding: 11px 22px; border-radius: 9px; font-size: 14px; font-weight: 800; cursor: pointer; font-family: inherit; transition: background .15s }
        .cb-btn-primary:hover:not(:disabled) { background: #0f2440 }
        .cb-btn-primary:disabled { background: #94a3b8; cursor: not-allowed }

        .cb-preview { margin-top: 26px }
        .cb-preview h3 { font-size: 13px; font-weight: 800; color: #475569; text-transform: uppercase; letter-spacing: .04em; margin-bottom: 10px }
        .cb-preview-card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 12px; padding: 16px }
        .cb-row { display: flex; justify-content: space-between; padding: 6px 0; border-bottom: 1px solid #f1f5f9; font-size: 13px; color: #1a3461 }
        .cb-row:last-child { border-bottom: 0 }
        .cb-row strong { font-size: 11px; color: #64748b; font-weight: 700; text-transform: uppercase; letter-spacing: .04em }
        .cb-row-highlight { background: rgba(0,168,181,0.06); padding: 10px 8px; border-radius: 6px; margin: 4px -8px; border-bottom: 0 }
        .cb-preview-instr { font-size: 12px; color: #475569; line-height: 1.5; margin-top: 10px; padding: 10px; background: #fff; border-radius: 8px }
      `}</style>
    </AdminLayout>
  )
}
